import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { storage } from './firebaseConfig'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import {
  getAdminAssets,
  getAssetSizes,
  addAsset,
  updateAsset,
  deleteAsset
} from '../../services/assetService'
import withAdminAuth from './withAdminAuth'
import classes from './asset_manager.module.css'

const TABS = [
  { key: 'frames', label: 'Frames', prefix: 'diy_frame', folder: 'live_templates/frames' },
  { key: 'samples', label: 'Samples', prefix: 'diy_sample', folder: 'live_templates/samples' },
  { key: 'decorations', label: 'Decorations', prefix: 'diy_decoration', folder: 'live_templates/decorations' },
  { key: 'backgrounds', label: 'Backgrounds', prefix: 'diy_background', folder: 'live_templates/backgrounds' },
  { key: 'stickers', label: 'Stickers', prefix: 'diy_sticker', folder: 'live_templates/stickers' },
  { key: 'colors', label: 'Colors', prefix: 'diy_color', folder: null }
]

const TAB_BY_KEY = TABS.reduce((acc, t) => ({ ...acc, [t.key]: t }), {})

function padNumber (n) {
  return String(n).padStart(4, '0')
}

function nextImageId (items, prefix) {
  const nums = (items || []).map((it) => {
    const m = String(it.id).match(/(\d+)$/)
    return m ? parseInt(m[1], 10) : 0
  })
  const next = nums.length ? Math.max(...nums) + 1 : 1
  return `${prefix}_${padNumber(next)}`
}

function formatSize (bytes) {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function AssetManager () {
  const [assets, setAssets] = useState(null)
  const [activeTab, setActiveTab] = useState('frames')
  const [sizes, setSizes] = useState({}) // blob -> bytes
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // { mode, asset?, items? }
  const [selected, setSelected] = useState(new Set()) // ids selected in the active tab

  const loadAssets = async () => {
    setLoading(true)
    try {
      const data = await getAdminAssets()
      setAssets(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAssets()
  }, [])

  const rows = useMemo(() => {
    if (!assets) return []
    const list = assets[activeTab] || []
    const q = search.trim().toLowerCase()
    return q ? list.filter((a) => String(a.id).toLowerCase().includes(q)) : list
  }, [assets, activeTab, search])

  // Fetch file sizes for the visible tab's blobs (colors have none).
  useEffect(() => {
    if (!assets) return
    const blobs = (assets[activeTab] || []).map((a) => a.blob).filter(Boolean)
    const missing = blobs.filter((b) => !(b in sizes))
    if (missing.length === 0) return
    let cancelled = false
    getAssetSizes(missing)
      .then((map) => {
        if (!cancelled) setSizes((prev) => ({ ...prev, ...map }))
      })
      .catch((err) => console.error('size fetch failed', err))
    return () => { cancelled = true }
    // Re-runs whenever the catalog or tab changes; `sizes` is read functionally.
  }, [assets, activeTab, sizes])

  // Clear the selection whenever the tab changes.
  useEffect(() => { setSelected(new Set()) }, [activeTab])

  const isColors = activeTab === 'colors'

  const allSelected = rows.length > 0 && rows.every((a) => selected.has(a.id))

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setSelected((prev) => {
      if (rows.length > 0 && rows.every((a) => prev.has(a.id))) return new Set()
      return new Set(rows.map((a) => a.id))
    })
  }

  const selectedAssets = rows.filter((a) => selected.has(a.id))

  return (
    <div className={classes.page}>
      <div className={classes.inner}>
        <h2 className={classes.title}>Asset Manager</h2>
        <p className={classes.subtitle}>Admin only · manage all DIY live-wallpaper assets</p>

        <div className={classes.tabs}>
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`${classes.tab} ${activeTab === t.key ? classes.tabActive : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
              {assets && <span className={classes.tabCount}>{(assets[t.key] || []).length}</span>}
            </button>
          ))}
        </div>

        <div className={classes.toolbar}>
          <input
            className={classes.search}
            placeholder={`Search ${activeTab} by id…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {selected.size > 0 && (
            <button
              className={classes.bulkDeleteBtn}
              onClick={() => setModal({ mode: 'bulk-delete', items: selectedAssets })}
            >
              Delete selected ({selected.size})
            </button>
          )}
          <button className={classes.addBtn} onClick={() => setModal({ mode: 'add' })}>
            + Add {TAB_BY_KEY[activeTab].label.replace(/s$/, '')}
          </button>
        </div>

        {loading
          ? (
          <div className={classes.loading}>Loading assets…</div>
            )
          : (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.checkCell}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th>Preview</th>
                  <th>Asset type</th>
                  <th>Asset id</th>
                  <th>Used by (templates)</th>
                  <th>File size</th>
                  <th>Format</th>
                  <th>Edit</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={9} className={classes.empty}>No assets.</td></tr>
                )}
                {rows.map((a) => (
                  <tr key={a.id} className={selected.has(a.id) ? classes.rowSelected : ''}>
                    <td className={classes.checkCell}>
                      <input
                        type="checkbox"
                        checked={selected.has(a.id)}
                        onChange={() => toggleOne(a.id)}
                        aria-label={`Select ${a.id}`}
                      />
                    </td>
                    <td>
                      {isColors
                        ? <span className={classes.swatch} style={{ background: a.hex }} />
                        : <img className={classes.preview} src={a.url} alt={a.id} loading="lazy" />}
                    </td>
                    <td>{activeTab}</td>
                    <td className={classes.idCell}>{a.id}{isColors ? ` · ${a.hex}` : ''}</td>
                    <td>
                      <span className={`${classes.usageBadge} ${a.usageTotal ? '' : classes.usageZero}`}>
                        {a.usageTotal}
                      </span>
                      <span className={classes.usageBreak}>
                        (正式 {a.usageLive} / 審核 {a.usageReview})
                      </span>
                    </td>
                    <td>{isColors ? '—' : formatSize(sizes[a.blob])}</td>
                    <td className={classes.fmt}>{isColors ? 'HEX' : (a.ext || '—')}</td>
                    <td>
                      <button className={classes.actionBtn} onClick={() => setModal({ mode: 'edit', asset: a })}>
                        Edit
                      </button>
                    </td>
                    <td>
                      <button
                        className={`${classes.actionBtn} ${classes.deleteBtn}`}
                        onClick={() => setModal({ mode: 'delete', asset: a })}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            )}
      </div>

      {modal && (
        <AssetModal
          modal={modal}
          tab={TAB_BY_KEY[activeTab]}
          assets={assets}
          onClose={() => setModal(null)}
          onDone={async () => { setModal(null); setSelected(new Set()); await loadAssets() }}
        />
      )}
    </div>
  )
}

// ── Modal: add / edit / delete ───────────────────────────────────────────────

function AssetModal ({ modal, tab, assets, onClose, onDone }) {
  const { mode, asset } = modal
  const isColors = tab.key === 'colors'

  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [hex, setHex] = useState(asset?.hex || '#000000')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState({ text: '', kind: 'info' })

  const handleFile = (e) => {
    const f = e.target.files[0]
    if (!f) return
    setFile(f)
    setPreview(URL.createObjectURL(f))
  }

  const say = (text, kind = 'info') => setStatus({ text, kind })

  // Upload a file to Firebase Storage at folder/{id}.{ext} and return { url, blob, ext }.
  const uploadImage = async (id) => {
    const ext = file.name.split('.').pop().toLowerCase()
    const blob = `${tab.folder}/${id}.${ext}`
    const storageRef = ref(storage, blob)
    const snap = await uploadBytes(storageRef, file)
    const url = await getDownloadURL(snap.ref)
    return { url, blob, ext }
  }

  const doAdd = async () => {
    setBusy(true)
    try {
      if (isColors) {
        const clean = hex.trim().toLowerCase()
        if (!/^#[0-9a-f]{6}$/.test(clean)) { say('Enter a valid #RRGGBB hex.', 'err'); setBusy(false); return }
        const id = `${tab.prefix}_${clean.slice(1)}`
        say('Saving color…')
        await addAsset('colors', id, undefined, undefined, undefined, clean)
      } else {
        if (!file) { say('Please choose a file.', 'err'); setBusy(false); return }
        const id = nextImageId(assets[tab.key], tab.prefix)
        say(`Uploading ${id}…`)
        const { url, blob, ext } = await uploadImage(id)
        say('Saving to database…')
        await addAsset(tab.key, id, url, blob, ext)
      }
      await onDone()
    } catch (err) {
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setBusy(false)
    }
  }

  const doEdit = async () => {
    setBusy(true)
    try {
      if (isColors) {
        const clean = hex.trim().toLowerCase()
        if (!/^#[0-9a-f]{6}$/.test(clean)) { say('Enter a valid #RRGGBB hex.', 'err'); setBusy(false); return }
        say('Saving color…')
        await updateAsset('colors', asset.id, { hex: clean })
      } else {
        if (!file) { say('Choose a replacement image first.', 'err'); setBusy(false); return }
        say('Uploading replacement…')
        const { url, blob, ext } = await uploadImage(asset.id)
        say('Saving to database…')
        await updateAsset(tab.key, asset.id, { url, blob, ext })
      }
      await onDone()
    } catch (err) {
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setBusy(false)
    }
  }

  const doDelete = async () => {
    setBusy(true)
    try {
      say('Deleting…')
      await deleteAsset(tab.key, asset.id)
      await onDone()
    } catch (err) {
      console.error(err)
      say(err.response?.data?.error || err.message, 'err')
      setBusy(false)
    }
  }

  const doBulkDelete = async () => {
    const items = modal.items || []
    setBusy(true)
    const failed = []
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      say(`Deleting ${i + 1}/${items.length}: ${it.id}…`)
      try {
        await deleteAsset(tab.key, it.id)
      } catch (err) {
        console.error(err)
        failed.push(it.id)
      }
    }
    if (failed.length) {
      say(`Deleted ${items.length - failed.length}/${items.length}. Failed: ${failed.join(', ')}`, 'err')
      setBusy(false)
      return
    }
    await onDone()
  }

  const statusClass = status.kind === 'err'
    ? classes.statusErr
    : status.kind === 'ok' ? classes.statusOk : classes.statusInfo

  return (
    <div className={classes.overlay} onClick={busy ? undefined : onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        {mode === 'bulk-delete'
          ? (
          <>
            <h3 className={classes.modalTitle}>Delete {(modal.items || []).length} {tab.label.toLowerCase()}?</h3>
            {(modal.items || []).some((it) => it.usageTotal > 0) && (
              <div className={classes.warn}>
                ⚠️ 有 <b>{(modal.items || []).filter((it) => it.usageTotal > 0).length}</b> 個選取的 asset 正被 template 使用，
                刪除後這些 template 會失去該素材。
              </div>
            )}
            <div className={classes.bulkList}>
              {(modal.items || []).map((it) => (
                <div key={it.id} className={classes.bulkListItem}>
                  <span className={classes.idCell}>{it.id}{tab.key === 'colors' ? ` · ${it.hex}` : ''}</span>
                  <span className={it.usageTotal > 0 ? classes.usageBreak : classes.usageZero}>
                    {it.usageTotal > 0 ? `使用中 ${it.usageTotal}` : '未使用'}
                  </span>
                </div>
              ))}
            </div>
            <p style={{ color: '#b7c0cd', fontSize: '0.88rem' }}>
              This removes each Firebase file and catalog entry. This cannot be undone.
            </p>
            {status.text && <div className={`${classes.statusMsg} ${statusClass}`}>{status.text}</div>}
            <div className={classes.modalActions}>
              <button className={classes.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
              <button className={classes.btnDanger} onClick={doBulkDelete} disabled={busy}>
                {busy ? 'Deleting…' : `Delete ${(modal.items || []).length}`}
              </button>
            </div>
          </>
            )
          : mode === 'delete'
            ? (
          <>
            <h3 className={classes.modalTitle}>Delete {asset.id}?</h3>
            {asset.usageTotal > 0 && (
              <div className={classes.warn}>
                ⚠️ 此 asset 目前被 <b>{asset.usageTotal}</b> 個 template 使用
                （正式 {asset.usageLive} / 審核 {asset.usageReview}）。
                刪除後這些 template 會失去此素材。
              </div>
            )}
            <p style={{ color: '#b7c0cd', fontSize: '0.88rem' }}>
              This removes the Firebase file{asset.blob ? ` (${asset.blob})` : ''} and the catalog entry. This cannot be undone.
            </p>
            {status.text && <div className={`${classes.statusMsg} ${statusClass}`}>{status.text}</div>}
            <div className={classes.modalActions}>
              <button className={classes.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
              <button className={classes.btnDanger} onClick={doDelete} disabled={busy}>
                {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </>
              )
            : (
          <>
            <h3 className={classes.modalTitle}>
              {mode === 'add' ? `Add ${tab.label.replace(/s$/, '')}` : `Edit ${asset.id}`}
            </h3>

            {mode === 'edit' && !isColors && (
              <div className={classes.field}>
                <span className={classes.label}>Asset id (fixed — referenced by templates)</span>
                <input className={`${classes.input} ${classes.inputReadonly}`} value={asset.id} readOnly />
              </div>
            )}

            {isColors
              ? (
              <>
                <div className={classes.colorHexPreview} style={{ background: /^#[0-9a-f]{6}$/i.test(hex.trim()) ? hex.trim() : '#000' }} />
                <div className={classes.field}>
                  <span className={classes.label}>Hex color (#RRGGBB)</span>
                  <input
                    className={classes.input}
                    value={hex}
                    onChange={(e) => setHex(e.target.value)}
                    placeholder="#a1b2c3"
                  />
                </div>
                {mode === 'add' && (
                  <div className={classes.field}>
                    <span className={classes.label}>Generated id</span>
                    <input
                      className={`${classes.input} ${classes.inputReadonly}`}
                      value={/^#[0-9a-f]{6}$/i.test(hex.trim()) ? `${tab.prefix}_${hex.trim().toLowerCase().slice(1)}` : '—'}
                      readOnly
                    />
                  </div>
                )}
              </>
                )
              : (
              <>
                {mode === 'add' && (
                  <div className={classes.field}>
                    <span className={classes.label}>Next id</span>
                    <input
                      className={`${classes.input} ${classes.inputReadonly}`}
                      value={assets ? nextImageId(assets[tab.key], tab.prefix) : '…'}
                      readOnly
                    />
                  </div>
                )}
                <div className={classes.field}>
                  <span className={classes.label}>{mode === 'edit' ? 'Replacement image' : 'Image file'}</span>
                  <input className={classes.input} type="file" accept="image/*" onChange={handleFile} />
                </div>
                {(preview || (mode === 'edit' && asset.url)) && (
                  <img className={classes.modalPreview} src={preview || asset.url} alt="preview" />
                )}
              </>
                )}

            {status.text && <div className={`${classes.statusMsg} ${statusClass}`}>{status.text}</div>}

            <div className={classes.modalActions}>
              <button className={classes.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
              <button
                className={classes.btnPrimary}
                onClick={mode === 'add' ? doAdd : doEdit}
                disabled={busy}
              >
                {busy ? 'Working…' : (mode === 'add' ? 'Add' : 'Save')}
              </button>
            </div>
          </>
              )}
      </div>
    </div>
  )
}

AssetModal.propTypes = {
  modal: PropTypes.shape({
    mode: PropTypes.oneOf(['add', 'edit', 'delete', 'bulk-delete']).isRequired,
    asset: PropTypes.object,
    items: PropTypes.array
  }).isRequired,
  tab: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    prefix: PropTypes.string.isRequired,
    folder: PropTypes.string
  }).isRequired,
  assets: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired
}

export default withAdminAuth(AssetManager)
